// What Marker can do to a scanned list. Gates use the scanner tools' vocabulary and the same
// resolvers, so an action and a tool with the same gate always agree. Every endpoint sits
// behind admin or operations, which the kiosk's operator token carries.

import { allowsInstance, userCanAccess } from '../../navigation/menuConfig';
import { INSTANCE, type Instance } from '../../instance';
import { capabilitiesFor } from '../capabilities';
import type { ToolViewer } from '../types';
import type { MarkerAction, MarkerApi, MarkerOrder, MarkerResult, MarkerRunOptions } from './types';

const BOTH = ['firefly', 'swarm'];

const list = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

const orders = (v: unknown): MarkerOrder[] =>
  Array.isArray(v)
    ? v.map((o: any) => ({
        so_number: String(o?.so_number ?? ''),
        order_type: String(o?.order_type ?? ''),
        status: String(o?.status ?? ''),
        count: Number(o?.count) || 0,
      }))
    : [];

export const toResult = (raw: any): MarkerResult => ({
  updated: list(raw?.updated),
  unchanged: list(raw?.unchanged),
  missing: list(raw?.missing),
  unarchived: list(raw?.unarchived),
  owner_name: raw?.owner_name ?? null,
  orders: orders(raw?.orders),
});

/** Every Marker request names the badged operator on a shared device so maintlist writes them. */
const send = (
  api: MarkerApi,
  path: string,
  body: Record<string, unknown>,
  operatorId: string | null,
  opts: MarkerRunOptions = {},
): Promise<any> =>
  api.post(path, {
    body: { ...body, ...(operatorId ? { operator_id: operatorId } : {}), ...(opts.confirm ? { confirm: true } : {}) },
  });

export const post = async (
  api: MarkerApi,
  path: string,
  body: Record<string, unknown>,
  operatorId: string | null,
  opts: MarkerRunOptions = {},
): Promise<MarkerResult> => toResult(await send(api, path, body, operatorId, opts));

export const MARKER_ACTIONS: MarkerAction[] = [
  {
    id: 'mark-customer',
    label: 'Mark customer',
    description: 'Give the drones to a customer, or return them to Internal Fleet.',
    require: 'operations',
    instances: BOTH,
    inputs: [
      { kind: 'customer', key: 'owner_org_id', label: 'Who gets these drones?' },
      {
        kind: 'choice',
        key: 'lease_or_sale',
        label: 'Sale or lease?',
        when: (v) => typeof v.owner_org_id === 'number',
        options: [
          { value: 'sale', label: 'Sale' },
          { value: 'lease', label: 'Lease' },
        ],
      },
    ],
    run: (api, serials, values, operatorId) => {
      const owner = typeof values.owner_org_id === 'number' ? values.owner_org_id : null;
      return post(
        api,
        '/scanner/marker/customer',
        {
          owner_org_id: owner,
          lease_or_sale: owner === null ? null : values.lease_or_sale,
          drones: serials.map((serial) => ({ serial })),
        },
        operatorId,
      );
    },
  },
  {
    id: 'mark-pyro',
    label: 'Mark pyro',
    description: 'Turn pyro on or off for the drones.',
    require: 'operations',
    // Firefleet only: SwarmSystems has no pyro.
    instances: ['firefly'],
    inputs: [
      {
        kind: 'choice',
        key: 'pyro_enabled',
        label: 'Pyro',
        options: [
          { value: true, label: 'Pyro on' },
          { value: false, label: 'Pyro off' },
        ],
      },
    ],
    run: (api, serials, values, operatorId) =>
      post(
        api,
        '/scanner/marker/pyro',
        { pyro_enabled: values.pyro_enabled === true, drones: serials.map((serial) => ({ serial })) },
        operatorId,
      ),
  },
  {
    id: 'mark-returned',
    label: 'Mark returned',
    description: 'Drones back from a customer. Each one knows its order.',
    require: 'operations',
    instances: BOTH,
    inputs: [],
    words: { done: 'returned', unchanged: 'already here' },
    run: async (api, serials, _values, operatorId, opts) => {
      const raw = await send(api, '/scanner/marker/returned', { drones: serials.map((serial) => ({ serial })) }, operatorId, opts);
      return toResult({
        updated: raw?.returned,
        unchanged: raw?.already_here,
        missing: raw?.missing,
        unarchived: raw?.unarchived,
        orders: raw?.orders,
      });
    },
  },
];

/** The actions this deployment has and this viewer may run, in registry order. */
export function actionsFor(
  viewer: ToolViewer,
  inst: Instance = INSTANCE,
  actions: MarkerAction[] = MARKER_ACTIONS,
): MarkerAction[] {
  const caps = capabilitiesFor(viewer);
  return actions.filter((a) => allowsInstance(a, inst) && userCanAccess(a, caps));
}
